/**
 * Client-side audit event collector.
 *
 * Buffers events and flushes them to POST /api/admin/audit/events in batches.
 * Silent on errors. Capped at 200 buffered events to prevent memory leaks.
 */
import { getCsrfToken } from "./csrf";

type ClientAuditEvent = {
  category: "ui" | "settings" | "system" | "job";
  action: string;
  component?: string;
  details?: Record<string, unknown>;
};

const MAX_BUFFER = 200;
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_BATCH_SIZE = 20;

let buffer: ClientAuditEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let started = false;

const flush = async (): Promise<void> => {
  if (buffer.length === 0) return;

  const batch = buffer.splice(0, FLUSH_BATCH_SIZE);
  try {
    const token = getCsrfToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(token ? { "x-csrf-token": token } : {}),
    };
    await fetch("/api/admin/audit/events", {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({ events: batch }),
    });
  } catch {
    // Silent failure — don't block UI
  }
};

export const trackEvent = (event: ClientAuditEvent): void => {
  if (!started) return;
  buffer.push(event);
  if (buffer.length > MAX_BUFFER) {
    buffer = buffer.slice(-MAX_BUFFER);
  }
  if (buffer.length >= FLUSH_BATCH_SIZE) {
    void flush();
  }
};

export const startAuditCollector = (): void => {
  if (started) return;
  started = true;
  flushTimer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
};

export const stopAuditCollector = (): void => {
  started = false;
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  void flush(); // final flush
};

/** Returns whether the collector is currently active. */
export const isCollectorStarted = (): boolean => started;

/**
 * Check if audit is enabled and start the collector if so.
 */
export const initAuditCollector = async (): Promise<void> => {
  try {
    const res = await fetch("/api/admin/audit/settings", { credentials: "include" });
    const json = (await res.json()) as { ok: boolean; data?: { enabled: boolean } };
    if (json.ok && json.data?.enabled) {
      startAuditCollector();
    }
  } catch {
    // Not admin or audit not available — no-op
  }
};
